import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Badge,
    Box,
    Button,
    CardActions,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    Paper,
    Popper,
    Stack,
    TextField,
    Typography,
    useMediaQuery
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import { useAppDispatch, useAppSelector, useNotificationUnreadCount, useNotifications, type NotificationListFilter } from '@/hooks';
import { setCurrentHospitalId } from '@/store/slices/hospital';
import { fetchActiveMeetingTranscript } from '@/store/slices/meeting-transcripts/meeting-transcripts-api';
import { setProfileDefaultHospital } from '@/store/slices/profile/profile-api';
import type { NotificationItem } from '@/types/notification';
import {
    MEETING_TRANSCRIPT_MATCHES_READY_TYPE_KEY,
    parseMeetingTranscriptMatchesReadyData,
    resolveMeetingTranscriptMatchesReadyPath
} from '@/utils/notifications/meetingTranscriptMatchesReady';
import { getMeetingTranscriptionPath } from '@/utils/meeting-transcription/match-verification';
import { isAdmin } from '@/utils/role';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';

// assets
import { IconBell } from '@tabler/icons-react';

const status: Array<{ value: NotificationListFilter; label: string }> = [
    {
        value: 'all',
        label: 'All Notification'
    },
    {
        value: 'unread',
        label: 'Unread'
    },
    {
        value: 'read',
        label: 'Read'
    }
];

// ==============================|| NOTIFICATION ||============================== //

const NotificationSection = () => {
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { profile } = useAppSelector(({ profile: profileState }) => profileState);
    const isAdminUser = isAdmin(profile);
    const { unreadCount, refreshUnreadCount } = useNotificationUnreadCount();

    const [open, setOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState<NotificationListFilter>('all');
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const navigatingRef = useRef(false);

    const { items, loading, loadingMore, hasMore, error, markingAsReadId, loadMore, handleMarkAsRead } = useNotifications({
        enabled: open,
        statusFilter,
        onMarkedAsRead: refreshUnreadCount
    });

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
            return;
        }
        setOpen(false);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current?.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const handleStatusChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setStatusFilter(event.target.value as NotificationListFilter);
    };

    const handleItemClick = useCallback(
        async (notification: NotificationItem) => {
            if (notification.typeKey !== MEETING_TRANSCRIPT_MATCHES_READY_TYPE_KEY || navigatingRef.current) {
                return;
            }

            const parsed = parseMeetingTranscriptMatchesReadyData(notification.data);
            if (!parsed) {
                navigate(getMeetingTranscriptionPath({ isAdminUser }));
                setOpen(false);
                return;
            }

            navigatingRef.current = true;
            try {
                if (!isAdminUser) {
                    try {
                        const { hospitalId } = await dispatch(setProfileDefaultHospital({ hospitalId: parsed.hospitalId })).unwrap();
                        dispatch(setCurrentHospitalId(hospitalId));
                    } catch {
                        // Continue navigation even if hospital switch fails.
                    }
                }

                const { active } = await dispatch(fetchActiveMeetingTranscript({ hospitalId: parsed.hospitalId })).unwrap();
                navigate(
                    resolveMeetingTranscriptMatchesReadyPath({
                        isAdminUser,
                        hospitalId: parsed.hospitalId,
                        meetingTranscriptInfoId: parsed.meetingTranscriptInfoId,
                        active
                    })
                );
            } catch {
                navigate(
                    getMeetingTranscriptionPath({
                        isAdminUser,
                        hospitalId: parsed.hospitalId,
                        hospitalContext: isAdminUser
                    })
                );
            } finally {
                navigatingRef.current = false;
                setOpen(false);
            }
        },
        [dispatch, isAdminUser, navigate]
    );

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    mr: 1.5,
                    [theme.breakpoints.down('md')]: {
                        mr: 1
                    }
                }}
            >
                <Badge
                    badgeContent={unreadCount}
                    color="error"
                    overlap="circular"
                    invisible={unreadCount === 0}
                    sx={{
                        '& .MuiBadge-badge': {
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            minWidth: 18,
                            height: 18,
                            padding: '0 4px'
                        }
                    }}
                >
                    <Avatar
                        variant="rounded"
                        sx={{
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,
                            transition: 'all .2s ease-in-out',
                            background: theme.palette.secondary.light,
                            color: theme.palette.secondary.dark,
                            '&[aria-controls="menu-list-grow"],&:hover': {
                                background: theme.palette.secondary.dark,
                                color: theme.palette.secondary.light
                            }
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        onClick={handleToggle}
                        color="inherit"
                    >
                        <IconBell stroke={1.5} size="1.3rem" />
                    </Avatar>
                </Badge>
            </Box>

            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [matchesXs ? 5 : 0, 20]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Grid size={{ xs: 12 }}>
                                                <Grid
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        pt: 2,
                                                        px: 2
                                                    }}
                                                >
                                                    <Grid>
                                                        <Stack direction="row" sx={{ gap: 2 }}>
                                                            <Typography variant="subtitle1">Notification</Typography>
                                                            <Chip
                                                                size="small"
                                                                label={String(unreadCount).padStart(2, '0')}
                                                                sx={{
                                                                    color: theme.palette.background.default,
                                                                    bgcolor: theme.palette.warning.dark
                                                                }}
                                                            />
                                                        </Stack>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <PerfectScrollbar
                                                    style={{
                                                        height: '100%',
                                                        maxHeight: 'calc(100vh - 205px)',
                                                        overflowX: 'hidden',
                                                        width: '350px'
                                                    }}
                                                >
                                                    <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <Grid size={{ xs: 12 }}>
                                                            <Box sx={{ px: 2, pt: 0.25 }}>
                                                                <TextField
                                                                    id="notification-status-filter"
                                                                    select
                                                                    fullWidth
                                                                    value={statusFilter}
                                                                    onChange={handleStatusChange}
                                                                    slotProps={{
                                                                        select: {
                                                                            native: true
                                                                        }
                                                                    }}
                                                                >
                                                                    {status.map((option) => (
                                                                        <option key={option.value} value={option.value}>
                                                                            {option.label}
                                                                        </option>
                                                                    ))}
                                                                </TextField>
                                                            </Box>
                                                        </Grid>
                                                        <Grid size={{ xs: 12 }} sx={{ p: 0 }}>
                                                            <Divider sx={{ my: 0 }} />
                                                        </Grid>
                                                    </Grid>
                                                    <NotificationList
                                                        items={items}
                                                        loading={loading}
                                                        error={error}
                                                        markingAsReadId={markingAsReadId}
                                                        onMarkAsRead={handleMarkAsRead}
                                                        onItemClick={handleItemClick}
                                                    />
                                                </PerfectScrollbar>
                                            </Grid>
                                        </Grid>
                                        {hasMore && (
                                            <>
                                                <Divider />
                                                <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                                                    <Button size="small" disableElevation onClick={loadMore} disabled={loadingMore}>
                                                        {loadingMore ? 'Loading...' : 'Load more'}
                                                    </Button>
                                                </CardActions>
                                            </>
                                        )}
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default NotificationSection;
